import io from "socket.io-client";

import {
  ACTIONS,
  AdminOperatorChatMessageEvent,
  AdminOperatorChatThreadStateEvent,
  GeneralMomentCreatedEventResponse,
  GeneralMomentUpdatedEventResponse,
  MOMENT_EMITTER_ACTIONS,
  OperatorGamePopulated,
} from "../shared/interfaces";
import { SERVER_BASE_URL } from "../utils/constants";
import {
  isGameClosedForOperatorMoments,
  isOperatorAssignedToGame,
  isUpcomingGameBlockedForOperator,
} from "../utils/gameAssignment";
import { GameServiceInstance } from "./game";
import { MomentServiceInstance } from "./moments";
import { NotificationServiceInstance } from "./notification";
import { NavigationServiceInstance } from "./navigation";
import { OperatorNotificationFeedInstance } from "./operatorNotificationFeed";
import { SessionServiceInstance } from "./session";
import {
  playOperatorAdminChatIncomingChimeIfFresh,
  shouldPlayAdminChatIncomingSound,
} from "../utils/operatorAdminChatSound";

function isPopulatedOperatorGame(
  game: GeneralMomentCreatedEventResponse["game"],
): game is OperatorGamePopulated {
  const g = game as OperatorGamePopulated;
  return Boolean(g?.team1?.name && g?.team2?.name);
}

function shouldNotifyNewDraftForViewer(
  game: GeneralMomentCreatedEventResponse["game"],
): boolean {
  const { isSuperAdmin, EmailAddress } = SessionServiceInstance;
  if (!EmailAddress) {
    return false;
  }
  if (isSuperAdmin) {
    return true;
  }
  const staffGame = game as { finishDate?: number | null; archived?: boolean; status?: string };
  if (isGameClosedForOperatorMoments(staffGame) || isUpcomingGameBlockedForOperator(staffGame)) {
    return false;
  }
  if (!isPopulatedOperatorGame(game)) {
    return false;
  }
  return isOperatorAssignedToGame(game, EmailAddress);
}

class SocketService {
  private socket: SocketIOClient.Socket

  private operatorLobbySocketListenersAttached = false;

  constructor() {
    this.socket = io(SERVER_BASE_URL);

    this.socket.on("connect", () => {
      console.log(`[Socket][on: connect] Connected to the ${SERVER_BASE_URL}`);
      this.joinOperatorsRoom();
    });
  }

  private joinOperatorsRoom = (): void => {
    this.socket.emit(ACTIONS.JOIN_OPERATOR_LOBBY, {}, () => {
      console.log("[Socket][emit: JOIN_OPERATOR_LOBBY] Joined the operators' room");
      if (!this.operatorLobbySocketListenersAttached) {
        this.operatorLobbySocketListenersAttached = true;
        this.onGeneralMomentCreate();
        this.onGeneralMomentUpdate();
        this.onAdminOperatorChatMessage();
        this.onAdminOperatorChatThreadState();
      }
    });
  }

  private onGeneralMomentCreate = (): void => {
    this.socket.on(
      MOMENT_EMITTER_ACTIONS.GENERAL_MOMENT_CREATED,
      (data: GeneralMomentCreatedEventResponse) => {
        console.log("[Socket][on: GENERAL_MOMENT_CREATED] data: ", data);
        this.addNewGeneralMoment(data);
      },
    );
  }

  private onGeneralMomentUpdate = (): void => {
    this.socket.on(
      MOMENT_EMITTER_ACTIONS.GENERAL_MOMENT_UPDATED,
      (data: GeneralMomentUpdatedEventResponse) => {
        console.log("[Socket][on: GENERAL_MOMENT_UPDATED] data: ", data);
        this.updateGeneralMoment(data);
      },
    );
  }

  private addNewGeneralMoment = (data: GeneralMomentCreatedEventResponse): void => {
    const updatedGameFromList = GameServiceInstance.updateDraftMoments(data.game._id);
    const onCurrentGameMomentsPage = MomentServiceInstance.socketAddDraftGeneralMoment(
      data.game._id,
      data.generalMoment,
    );

    const listGame = updatedGameFromList
      ?? GameServiceInstance.games.find((g) => g._id === data.game._id) ?? null;
    const shouldNotify = shouldNotifyNewDraftForViewer(listGame ?? data.game);

    if (!shouldNotify) {
      return;
    }

    const socketGame = isPopulatedOperatorGame(data.game) ? data.game : null;
    const gameForNotification = socketGame || updatedGameFromList;
    if (!gameForNotification?.team1?.name) {
      return;
    }

    const openDraft = (): void => {
      if (onCurrentGameMomentsPage) {
        MomentServiceInstance.selectMoment(data.generalMoment);
        return;
      }
      NavigationServiceInstance.goToGameMoments(
        data.game._id,
        data.generalMoment._id,
      );
    };

    NotificationServiceInstance.showNewDraftMomentCaptured({
      game: gameForNotification,
      generalMoment: data.generalMoment,
      onView: openDraft,
    });

    const t1 = gameForNotification.team1?.name ?? "Team 1";
    const t2 = gameForNotification.team2?.name ?? "Team 2";
    OperatorNotificationFeedInstance.recordDraftMomentCaptured({
      gameLabel: `${t1} vs ${t2}`,
      gameId: String(data.game._id),
      generalMomentId: data.generalMoment._id,
    });
  }

  private onAdminOperatorChatMessage = (): void => {
    this.socket.on(
      MOMENT_EMITTER_ACTIONS.ADMIN_OPERATOR_CHAT_MESSAGE,
      (data: AdminOperatorChatMessageEvent) => {
        window.dispatchEvent(
          new CustomEvent<AdminOperatorChatMessageEvent>("momento:admin-operator-chat-message", {
            detail: data,
          }),
        );
        const { EmailAddress: email, isSuperAdmin } = SessionServiceInstance;
        const msgId = data?.message?._id;
        if (
          email
          && msgId
          && shouldPlayAdminChatIncomingSound(data, email, isSuperAdmin)
        ) {
          playOperatorAdminChatIncomingChimeIfFresh(String(msgId));
        }
        if (email) {
          window.setTimeout(() => {
            OperatorNotificationFeedInstance.refreshSnapshot(email).catch(() => undefined);
          }, 0);
        }
      },
    );
  }

  private onAdminOperatorChatThreadState = (): void => {
    this.socket.on(
      MOMENT_EMITTER_ACTIONS.ADMIN_OPERATOR_CHAT_THREAD_STATE,
      (data: AdminOperatorChatThreadStateEvent) => {
        window.dispatchEvent(
          new CustomEvent<AdminOperatorChatThreadStateEvent>("momento:admin-chat-thread-state", {
            detail: data,
          }),
        );
        const email = SessionServiceInstance.EmailAddress;
        if (email) {
          window.setTimeout(() => {
            OperatorNotificationFeedInstance.refreshSnapshot(email).catch(() => undefined);
          }, 0);
        }
      },
    );
  }

  private updateGeneralMoment = (data: GeneralMomentUpdatedEventResponse): void => {
    const gMid = data.generalMomentId._id;
    const handledOnMomentsPage = MomentServiceInstance.socketUpdateGeneralMoment(
      gMid,
      data.moment,
    );

    if (handledOnMomentsPage) {
      return;
    }

    const fromPayload = data.game && isPopulatedOperatorGame(data.game)
      ? data.game
      : null;
    const gameIdHint = data.game && "_id" in data.game ? String(data.game._id) : "";
    const fromList = gameIdHint
      ? GameServiceInstance.games.find((g) => g._id === gameIdHint) ?? null
      : null;
    const gameForNotification = fromPayload || fromList;

    if (!gameForNotification?.team1?.name) {
      return;
    }

    if (!shouldNotifyNewDraftForViewer(gameForNotification)) {
      return;
    }

    const gameId = gameForNotification._id;

    NotificationServiceInstance.showUpdatedMomentNotification(
      gMid,
      data.moment._id,
      data.moment.camera,
      (_gMomentId, _momentId) => {
        NavigationServiceInstance.goToGameMoments(gameId, gMid);
      },
    );

    const u1 = gameForNotification.team1?.name ?? "Team 1";
    const u2 = gameForNotification.team2?.name ?? "Team 2";
    OperatorNotificationFeedInstance.recordDraftMomentUpdated({
      gameLabel: `${u1} vs ${u2}`,
      gameId: String(gameId),
      generalMomentId: gMid,
      cameraName: data.moment.camera,
    });
  }
}

export const SocketServiceInstance = new SocketService();
